import React, { useState, useRef } from 'react';
import styled from 'styled-components';

const InputContainer = styled.div`
  position: relative;
  padding: 16px;
  background-color: var(--clr-bg-secondary, #2a2a2a);
  flex-shrink: 0; /* Prevent shrinking when keyboard appears */
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  max-width: 1000px;
  margin: 0 auto;
`;

const InputField = styled.textarea`
  flex: 1;
  background-color: var(--clr-bg, #1a1a1a);
  border: 1px solid var(--clr-border, #444);
  border-radius: 20px;
  padding: 10px 16px;
  color: var(--clr-text, #ffffff);
  font-size: 0.95rem;
  font-family: inherit;
  resize: none;
  max-height: 120px;
  min-height: 40px;
  line-height: 1.4;

  &::placeholder {
    color: var(--clr-text-secondary, #aaa);
  }

  &:focus {
    outline: none;
    border-color: var(--clr-primary, #007bff);
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--clr-border, #444);
    border-radius: 3px;
  }
`;

const SendButton = styled.button`
  background-color: var(--clr-primary, #007bff);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  transform-origin: center;

  &:hover:not(:disabled) {
    background-color: var(--clr-primary-hover, #0056b3);
    transform: scale(1.15) translateY(-2px);
    filter: drop-shadow(0 6px 12px rgba(0, 123, 255, 0.3));
    color: #ffffff;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    filter: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: flex-end;
  flex-shrink: 0;
  gap: 4px;
`;

const CharCount = styled.div`
  font-size: 0.75rem;
  color: ${(props) =>
    props.$nearLimit
      ? 'var(--clr-warning, #ffa500)'
      : 'var(--clr-text-secondary, #aaa)'};
  text-align: center;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  min-height: 18px;
`;

function DMInputBar({ onSendMessage, disabled = false }) {
  const [message, setMessage] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const maxLength = 300;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!message.trim() || disabled) {
      return;
    }

    onSendMessage(message);
    setMessage('');

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setMessage(value);

      // Auto-resize textarea
      const textarea = e.target;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  const canSend = message.trim().length > 0 && !disabled;
  const nearLimit = message.length > maxLength * 0.8;

  return (
    <InputContainer ref={containerRef}>
      <InputWrapper>
        <InputField
          ref={inputRef}
          value={message}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={disabled ? 'Cannot send messages' : 'Type a message...'}
          disabled={disabled}
          maxLength={maxLength}
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="sentences"
          spellCheck="true"
          inputMode="text"
          enterKeyHint="send"
        />
        <ButtonContainer>
          <SendButton
            onClick={handleSubmit}
            disabled={!canSend}
            title='Send message (Enter)'
          >
            ➤
          </SendButton>
          <CharCount $nearLimit={nearLimit} $visible={message.length > 0}>
            {message.length > 0 ? `${message.length}/${maxLength}` : ''}
          </CharCount>
        </ButtonContainer>
      </InputWrapper>
    </InputContainer>
  );
}

export default DMInputBar;
